import React, { useCallback, useState, createContext, useContext } from 'react';
import { MessageSquareIcon, PackageIcon, UsersIcon, SparklesIcon, CheckCircleIcon, SettingsIcon } from 'lucide-react';
export type ActivityType = 'chat' | 'order' | 'people' | 'insight' | 'milestone' | 'system';
export interface ActivityEvent {
  id: string;
  accountId: string;
  type: ActivityType;
  message: string;
  timestamp: Date;
  link?: string;
  metadata?: Record<string, any>;
}
interface ActivityFeedContextType {
  getActivities: (accountId: string) => ActivityEvent[];
  addActivity: (accountId: string, activity: Omit<ActivityEvent, 'id' | 'timestamp' | 'accountId'>) => void;
  clearActivities: (accountId: string) => void;
}
const ActivityFeedContext = createContext<ActivityFeedContextType | undefined>(undefined);
export function ActivityFeedProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [activities, setActivities] = useState<Record<string, ActivityEvent[]>>({});
  const getActivities = useCallback((accountId: string): ActivityEvent[] => {
    return activities[accountId] || [];
  }, [activities]);
  const addActivity = useCallback((accountId: string, activity: Omit<ActivityEvent, 'id' | 'timestamp' | 'accountId'>) => {
    setActivities(prev => ({
      ...prev,
      [accountId]: [...(prev[accountId] || []), {
        ...activity,
        id: `activity-${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        accountId
      }]
    }));
  }, []);
  const clearActivities = useCallback((accountId: string) => {
    setActivities(prev => {
      const next = {
        ...prev
      };
      delete next[accountId];
      return next;
    });
  }, []);
  return <ActivityFeedContext.Provider value={{
    getActivities,
    addActivity,
    clearActivities
  }} data-id="element-1533">
      {children}
    </ActivityFeedContext.Provider>;
}
export function useActivityFeed() {
  const context = useContext(ActivityFeedContext);
  if (!context) {
    throw new Error('useActivityFeed must be used within ActivityFeedProvider');
  }
  return context;
}
/**
 * Get icon component for activity type
 */
export function getActivityIcon(type: ActivityType): React.ReactNode {
  const colorClass = getActivityColor(type);
  switch (type) {
    case 'chat':
      return <MessageSquareIcon className={`${colorClass} w-3.5 h-3.5`} data-id="element-1534" />;
    case 'order':
      return <PackageIcon className={`${colorClass} w-3.5 h-3.5`} data-id="element-1535" />;
    case 'people':
      return <UsersIcon className={`${colorClass} w-3.5 h-3.5`} data-id="element-1536" />;
    case 'insight':
      return <SparklesIcon className={`${colorClass} w-3.5 h-3.5`} data-id="element-1537" />;
    case 'milestone':
      return <CheckCircleIcon className={`${colorClass} w-3.5 h-3.5`} data-id="element-1538" />;
    case 'system':
      return <SettingsIcon className={`${colorClass} w-3.5 h-3.5`} data-id="element-1539" />;
  }
}
/**
 * Get color class for activity type
 */
export function getActivityColor(type: ActivityType): string {
  const colors = {
    chat: 'text-blue-400',
    order: 'text-green-400',
    people: 'text-purple-400',
    insight: 'text-amber-400',
    milestone: 'text-pink-400',
    system: 'text-slate-400'
  };
  return colors[type];
}
/**
 * Group activities by date
 */
export function groupActivitiesByDate(activities: ActivityEvent[]): Record<string, ActivityEvent[]> {
  const groups: Record<string, ActivityEvent[]> = {};
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const thisWeek = new Date(today);
  thisWeek.setDate(thisWeek.getDate() - 7);
  activities.forEach(activity => {
    const activityDate = new Date(activity.timestamp);
    const activityDay = new Date(activityDate.getFullYear(), activityDate.getMonth(), activityDate.getDate());
    let groupKey: string;
    if (activityDay.getTime() === today.getTime()) {
      groupKey = 'Today';
    } else if (activityDay.getTime() === yesterday.getTime()) {
      groupKey = 'Yesterday';
    } else if (activityDay >= thisWeek) {
      groupKey = 'Earlier This Week';
    } else {
      groupKey = activityDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(activity);
  });
  return groups;
}